import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardActions,
  Avatar,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Share as SocialIcon,
  Mail as DirectMailIcon,
  Campaign as CampaignIcon,
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const statusColors = {
  draft: 'default',
  scheduled: 'info',
  active: 'success',
  paused: 'warning',
  completed: 'success',
  cancelled: 'error',
};

const typeOptions = [
  { value: 'email', label: 'Email', icon: <EmailIcon /> },
  { value: 'sms', label: 'SMS', icon: <SmsIcon /> },
  { value: 'social_media', label: 'Social Media', icon: <SocialIcon /> },
  { value: 'direct_mail', label: 'Direct Mail', icon: <DirectMailIcon /> },
  { value: 'multi_channel', label: 'Multi-Channel', icon: <CampaignIcon /> },
];

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function CampaignList({
  campaigns = [],
  loading = false,
  onSelect,
  onEdit,
  onDelete,
  onDuplicate,
}) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCampaignForMenu, setSelectedCampaignForMenu] = useState(null);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <EmailIcon />;
      case 'sms': return <SmsIcon />;
      case 'social_media': return <SocialIcon />;
      case 'direct_mail': return <DirectMailIcon />;
      case 'multi_channel': return <CampaignIcon />;
      default: return <CampaignIcon />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckIcon />;
      case 'draft': return <InfoIcon />;
      case 'scheduled': return <ScheduleIcon />;
      case 'paused': return <WarningIcon />;
      case 'completed': return <CheckIcon />;
      case 'cancelled': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  // Filter campaigns based on search and filters
  const filteredCampaigns = useMemo(() => campaigns.filter((campaign) => {
    const matchesSearch = searchTerm === ''
        || campaign.name.toLowerCase().includes(searchTerm.toLowerCase())
        || campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
        || (campaign.tags || []).some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    const matchesType = typeFilter === 'all' || campaign.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  }), [campaigns, searchTerm, statusFilter, typeFilter]);

  // DataGrid columns configuration
  const columns = [
    {
      field: 'name',
      headerName: 'Campaign Name',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            width: 32,
            height: 32,
          }}
          >
            {getTypeIcon(params.row.type)}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.type.replace('_', ' ').toUpperCase()}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={statusColors[params.value] || 'default'}
          size="small"
          variant="outlined"
          icon={getStatusIcon(params.value)}
        />
      ),
    },
    {
      field: 'analytics',
      headerName: 'Performance',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Open:
              {' '}
              {params.value.openRate?.toFixed(1)}
              %
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click:
              {' '}
              {params.value.clickRate?.toFixed(1)}
              %
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={params.value.openRate || 0}
            sx={{ height: 4, borderRadius: 2 }}
          />
        </Box>
      ),
    },
    {
      field: 'targetAudience',
      headerName: 'Recipients',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {params.value.actualRecipients || params.value.estimatedRecipients || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            recipients
          </Typography>
        </Box>
      ),
    },
    {
      field: 'schedule',
      headerName: 'Schedule',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value.type === 'immediate' ? 'Immediate'
              : params.value.type === 'scheduled' ? 'Scheduled'
                : params.value.type === 'recurring' ? 'Recurring' : 'Not set'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 0.8,
      minWidth: 120,
      type: 'date',
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => onSelect(params.row)}
              sx={{ color: 'primary.main' }}
            >
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="More Actions">
            <IconButton
              size="small"
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
                setSelectedCampaignForMenu(params.row);
              }}
            >
              <MoreIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaignForMenu(null);
  };

  const handleMenuAction = (action) => {
    if (!selectedCampaignForMenu) return;

    switch (action) {
      case 'view':
        onSelect(selectedCampaignForMenu);
        break;
      case 'edit':
        onEdit(selectedCampaignForMenu);
        break;
      case 'duplicate':
        onDuplicate(selectedCampaignForMenu._id);
        break;
      case 'delete':
        onDelete(selectedCampaignForMenu._id);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  return (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column', p: 2,
    }}
    >
      {/* Header with search and filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Campaigns (
            {filteredCampaigns.length}
            )
          </Typography>
          <Box sx={{ flex: 1 }} />
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 400 }}
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {option.icon}
                    <span>{option.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* DataGrid */}
      <Paper elevation={1} sx={{ flex: 1 }}>
        <DataGrid
          rows={filteredCampaigns}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
          }}
          components={{ Toolbar: GridToolbar }}
          onRowClick={(params) => onSelect(params.row)}
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleMenuAction('view')}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('edit')}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Campaign</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('duplicate')}>
          <ListItemIcon>
            <DuplicateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate Campaign</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleMenuAction('delete')}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Campaign</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default CampaignList;
